import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "../i18n";
import Wordmark from "../routes/newApp/assets/juhuu-wordmark.png";
import AppStoreBadge from "../routes/newApp/assets/app-store-badge.webp";
import GooglePlayBadge from "../routes/newApp/assets/google-play-badge.webp";

const STORE = {
  ios: "https://apps.apple.com/at/app/juhuu/id1603108125",
  android: "https://play.google.com/store/apps/details?id=com.juhuu_bikebox.juhuu",
  web: "https://app.juhuu.app/",
};

export default function Navbar() {
  const { t, selectedLanguage, setLanguage } = useTranslation();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll while the drawer is open
  useEffect(() => {
    document.body.style.overflow = sheetOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [sheetOpen]);

  // Close the drawer on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setSheetOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const PRODUCTS = [
    { href: "/app", label: t("components.nav.products.app"), sub: t("components.nav.products.appSub") },
    { href: "/dashboard", label: t("components.nav.products.dashboard"), sub: t("components.nav.products.dashboardSub") },
    { href: "/locking-systems", label: t("components.nav.products.locking"), sub: t("components.nav.products.lockingSub") },
  ];

  const INDUSTRIES = [
    { href: "/industries#parking", label: t("components.nav.industries.bikeParking") },
    { href: "/industries#rental", label: t("components.nav.industries.bikeRental") },
    { href: "/industries#custom", label: t("components.nav.industries.other") },
  ];

  const langBtn = (code: string) =>
    `rounded-full px-2.5 py-1 text-[.74rem] font-bold transition-all ${
      selectedLanguage === code
        ? "bg-white text-ink shadow-sm"
        : "text-muted hover:text-ink"
    }`;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-[150] border-b transition-colors ${
          scrolled
            ? "border-line bg-white/[.88] backdrop-blur-xl"
            : "border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-4 py-3.5 md:px-8">
          <Link to="/" aria-label="JUHUU" className="flex-shrink-0">
            <img src={Wordmark} alt="JUHUU" className="h-[22px] w-auto" />
          </Link>

          {/* Desktop links */}
          <div className="hidden items-center gap-6 lg:flex">
            <Link className="text-[.92rem] font-semibold text-ink-2 transition-colors hover:text-juhuu" to="/app">
              {t("components.nav.products.app")}
            </Link>
            <Link className="text-[.92rem] font-semibold text-ink-2 transition-colors hover:text-juhuu" to="/dashboard">
              {t("components.nav.products.dashboard")}
            </Link>
            <Link className="text-[.92rem] font-semibold text-ink-2 transition-colors hover:text-juhuu" to="/locking-systems">
              {t("components.nav.products.locking")}
            </Link>

            {/* Industries dropdown */}
            <div className="group relative">
              <span className="inline-flex cursor-pointer items-center gap-1 text-[.92rem] font-semibold text-ink-2 transition-colors group-hover:text-juhuu">
                {t("components.nav.industriesLabel")}
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  className="opacity-60 transition-transform group-hover:rotate-180 group-hover:opacity-100"
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </span>
              <div className="invisible absolute left-1/2 top-full w-56 -translate-x-1/2 translate-y-2 rounded-2xl border border-line bg-white p-2 opacity-0 shadow-xl transition-all group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                {INDUSTRIES.map((b) => (
                  <Link
                    key={b.href}
                    to={b.href}
                    className="block rounded-lg px-3 py-2.5 text-[.9rem] font-semibold text-ink-2 transition-colors hover:bg-juhuu-soft hover:text-juhuu-deep"
                  >
                    {b.label}
                  </Link>
                ))}
              </div>
            </div>

            <Link className="text-[.92rem] font-semibold text-ink-2 transition-colors hover:text-juhuu" to="/references">
              {t("components.nav.references")}
            </Link>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-3">
            <div
              role="group"
              aria-label="Language"
              className="inline-flex items-center gap-0.5 rounded-full bg-[#faf9fd] p-[3px]"
            >
              <button type="button" className={langBtn("de")} onClick={() => setLanguage("de")}>
                DE
              </button>
              <button type="button" className={langBtn("en")} onClick={() => setLanguage("en")}>
                EN
              </button>
            </div>

            <a
              href={STORE.web}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden rounded-full border border-line-strong px-4 py-2.5 text-[.9rem] font-semibold text-ink transition-colors hover:border-juhuu hover:text-juhuu lg:inline-flex"
            >
              WebApp
            </a>

            <Link
              to="/meeting"
              className="inline-flex items-center rounded-full bg-juhuu px-5 py-2.5 text-[.9rem] font-semibold text-white shadow-[0_14px_30px_-10px_rgba(109,22,250,.55)] transition-all hover:-translate-y-0.5 hover:bg-juhuu-ink"
            >
              {t("components.nav.bookDemo")}
            </Link>

            <button
              type="button"
              aria-label="Menu"
              aria-expanded={sheetOpen}
              onClick={() => setSheetOpen(true)}
              className="p-1.5 text-ink lg:hidden"
            >
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18M3 12h18M3 18h18" />
              </svg>
            </button>
          </div>
        </div>
      </nav>

      {/* ── Mobile drawer (right side) ─────────────────────── */}
      <div className={`fixed inset-0 z-[200] ${sheetOpen ? "visible" : "invisible"}`}>
        <div
          onClick={() => setSheetOpen(false)}
          className={`absolute inset-0 bg-ink/40 transition-opacity duration-300 ${
            sheetOpen ? "opacity-100" : "opacity-0"
          }`}
        />
        <div
          className={`absolute bottom-0 right-0 top-0 w-[300px] max-w-[85vw] overflow-y-auto bg-white p-6 transition-transform duration-300 ${
            sheetOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between pb-4">
            <Link to="/" onClick={() => setSheetOpen(false)}>
              <img src={Wordmark} alt="JUHUU" className="h-6 w-auto" />
            </Link>
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setSheetOpen(false)}
              className="p-1 text-muted hover:text-ink"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="mt-3 text-[.72rem] font-bold uppercase tracking-[.1em] text-muted">
            {t("components.nav.productLabel")}
          </div>
          {PRODUCTS.map((p) => (
            <Link
              key={p.href}
              to={p.href}
              onClick={() => setSheetOpen(false)}
              className="block border-b border-line py-3 text-[1.05rem] font-semibold text-ink"
            >
              {p.label}
            </Link>
          ))}

          <div className="mt-5 text-[.72rem] font-bold uppercase tracking-[.1em] text-muted">
            {t("components.nav.industriesLabel")}
          </div>
          {INDUSTRIES.map((b) => (
            <Link
              key={b.href}
              to={b.href}
              onClick={() => setSheetOpen(false)}
              className="block border-b border-line py-3 text-[1.05rem] font-semibold text-ink"
            >
              {b.label}
            </Link>
          ))}

          <div className="mt-5 text-[.72rem] font-bold uppercase tracking-[.1em] text-muted">
            {t("components.nav.companyLabel")}
          </div>
          <Link
            to="/references"
            onClick={() => setSheetOpen(false)}
            className="block border-b border-line py-3 text-[1.05rem] font-semibold text-ink"
          >
            {t("components.nav.references")}
          </Link>
          <Link
            to="/team"
            onClick={() => setSheetOpen(false)}
            className="block border-b border-line py-3 text-[1.05rem] font-semibold text-ink"
          >
            {t("components.nav.about")}
          </Link>

          <div className="mt-6 flex flex-col gap-2.5">
            <a
              href={STORE.web}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full rounded-full border border-line-strong px-5 py-3 text-center font-semibold text-ink"
            >
              WebApp
            </a>
            <Link
              to="/meeting"
              onClick={() => setSheetOpen(false)}
              className="w-full rounded-full bg-juhuu px-5 py-3 text-center font-semibold text-white"
            >
              {t("components.nav.bookDemo")}
            </Link>
          </div>

          <div className="mt-6 text-[.72rem] font-bold uppercase tracking-[.1em] text-muted">
            {t("components.nav.getTheApp")}
          </div>
          <div className="mt-2.5 flex flex-wrap gap-2">
            <a href={STORE.ios} target="_blank" rel="noopener noreferrer">
              <img src={AppStoreBadge} alt={t("routes.app.appStoreAlt")} className="h-[42px] w-auto" />
            </a>
            <a href={STORE.android} target="_blank" rel="noopener noreferrer">
              <img src={GooglePlayBadge} alt={t("routes.app.googlePlayAlt")} className="h-[42px] w-auto" />
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
